import { Injectable } from '@angular/core';
import moment from 'moment';
import {
  DeleteFileByIdData,
  ExcelDataPayload,
  ExcelRowData,
  FileResponseModel,
  GenerateHdfcIrmReportRequestContent,
  GenerateHdfcRequestLetterRequestContent,
  MimeType,
  Purpose,
  RequestLetterPayload,
} from 'src/app/api/autogenerated/types.gen';
import { DocumentDetails } from 'src/app/types/DocumentDetails';
import { FilePurpose } from 'src/app/types/FileTypes';
import { Invoice } from 'src/app/types/Invoice';
import { PaymentMethodDetails } from 'src/app/types/PaymentDetails';
import { Payout } from 'src/app/types/Payout';
import { Profile } from 'src/app/types/Profile';
import { FileUploadResponse } from 'src/app/types/responses/FileUploadResponse';
import { SharedProfileData } from '../../data/shared-profile-data';
import { IFileService } from '../IFileService';
import { OpenApiService } from '../open-api.service';

@Injectable({
  providedIn: 'root',
})
export class FileService implements IFileService {
  constructor(
    private sharedProfileData: SharedProfileData,
    private openapiService: OpenApiService,
  ) {}

  private getImageType(fileType: string): MimeType {
    if (fileType === 'image/jpeg') {
      return 'JPEG';
    } else if (fileType === 'image/png') {
      return 'PNG';
    } else if (fileType === 'application/pdf') {
      return 'PDF';
    } else if (fileType == 'application/vnd.ms-excel') {
      return 'MS_EXCEL';
    } else if (
      fileType ==
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      return 'MS_EXCEL';
    }
    throw new Error('Unsupported file type');
  }

  private getPurposeType(type: FilePurpose): Purpose {
    switch (type) {
      case FilePurpose.GST_IN_DOCUMENT:
        return 'GST_IN_DOCUMENT';
      case FilePurpose.DEED_DOCUMENT:
        return 'DEED_DOCUMENT';
      case FilePurpose.IEC_DOCUMENT:
        return 'IEC_DOCUMENT';
      case FilePurpose.NO_GST_AND_IEC_DOCUMENT:
        return 'NO_GST_AND_IEC_DOCUMENT';
      case FilePurpose.PAYOUT_FIRC:
        return 'PAYOUT_FIRC';
      case FilePurpose.INVOICE_DOCUMENT:
        return 'INVOICE_DOCUMENT';
      case FilePurpose.COMPANY_LOGO:
        return 'COMPANY_LOGO';
      case FilePurpose.ADDITIONAL_INVOICE_DOCUMENT:
        return 'ADDITIONAL_INVOICE_DOCUMENT';
      case FilePurpose.KYB_DOC_OLD_FIRC:
        return 'KYB_DOC_OLD_FIRC';
      case FilePurpose.KYB_DOC_OLD_INVOICE:
        return 'KYB_DOC_OLD_INVOICE';
      case FilePurpose.KYB_DOC_CERTIFICATE_ISSUED_BY_MUNICIPAL_AUTHORITY:
        return 'KYB_DOC_CERTIFICATE_ISSUED_BY_MUNICIPAL_AUTHORITY';
      case FilePurpose.KYB_DOC_UDYAM_CERTIFICATE:
        return 'KYB_DOC_UDYAM_CERTIFICATE';
      case FilePurpose.KYB_DOC_CERTIFICATE_ISSUED_BY_TAX_AUTHORITY:
        return 'KYB_DOC_CERTIFICATE_ISSUED_BY_TAX_AUTHORITY';
      case FilePurpose.KYB_DOC_UTILITY_BILL:
        return 'KYB_DOC_UTILITY_BILL';
      case FilePurpose.KYB_DOC_CONTRACT_AGREEMENT:
        return 'KYB_DOC_CONTRACT_AGREEMENT';
      case FilePurpose.USER_PROFILE_PICTURE:
        return 'USER_PROFILE_PICTURE';
      case FilePurpose.UBO_PAN:
        return 'UBO_PAN';
      case FilePurpose.UBO_HOME_ADDRESS_PROOF:
        return 'UBO_HOME_ADDRESS_PROOF';

      default:
        throw new Error('Unsupported file type');
    }
  }

  async uploadFile(
    file: File,
    type: FilePurpose,
    customerId?: string,
  ): Promise<FileUploadResponse> {
    const user = this.sharedProfileData.getUserProfile();
    const resolvedCustomerId = customerId ?? user?.businessId;
    if (!resolvedCustomerId) {
      throw new Error('Customer ID is required to upload files');
    }

    const queryFilters: {
      purpose: Purpose;
      mime_type: MimeType;
      file_name: string;
      size: number;
    } = {
      purpose: this.getPurposeType(type),
      mime_type: this.getImageType(file.type),
      file_name: file.name,
      size: file.size,
    };

    const fileBlob = new Blob([file], { type: file.type });

    const response = await this.openapiService.uploadFile(
      resolvedCustomerId,
      fileBlob,
      queryFilters,
    );

    if (!response.success) {
      throw new Error(response.error);
    }

    return { fileId: response.data.file_id };
  }

  private getFileExtension(mimeType: string | undefined): string {
    switch (mimeType) {
      case 'image/jpeg':
        return 'jpg';
      case 'image/png':
        return 'png';
      case 'application/pdf':
        return 'pdf';
      case 'application/vnd.ms-excel':
        return 'xls';
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return 'xlsx';
      default:
        throw new Error('Unsupported file type');
    }
  }

  toDocumentDetails(
    fileDetails: FileResponseModel,
    fileBinary: Blob,
    purpose: FilePurpose,
  ): DocumentDetails {
    const fileName: string = fileDetails.name
      ? fileDetails.name
      : 'file' +
        fileDetails.id +
        '.' +
        this.getFileExtension(fileDetails.mime_type);

    return {
      fileId: fileDetails.id,
      name: fileDetails.name,
      mediaType: fileDetails.mime_type,
      type: FilePurpose[purpose].toString(),
      size: fileDetails.size,
      url: URL.createObjectURL(fileBinary),
      file: new File([fileBinary], fileName, { type: fileDetails.mime_type }),
    };
  }

  private toDocumentDetailsFromList(
    fileDetails: FileResponseModel,
  ): DocumentDetails {
    return {
      fileId: fileDetails.id,
      name: fileDetails.name,
      mediaType: fileDetails.mime_type,
      size: fileDetails.size,
      url: fileDetails.url,
      type: fileDetails.purpose,
      submittedAt: fileDetails.submitted_at
        ? new Date(fileDetails.submitted_at)
        : undefined,
    };
  }

  async getFilesByCustomerId(customerId: string): Promise<DocumentDetails[]> {
    const response = await this.openapiService.getFilesByCustomerId(customerId);

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch customer files');
    }

    return (response.data.documents ?? []).map((file) =>
      this.toDocumentDetailsFromList(file),
    );
  }

  async getCustomerFileContent(
    customerId: string,
    fileId: string,
    purpose: Purpose,
    fileMetadata: Pick<DocumentDetails, 'name' | 'mediaType' | 'size'> = {},
  ): Promise<DocumentDetails> {
    console.log('Customer ID: ', customerId);
    console.log('File ID: ', fileId);
    console.log('Purpose: ', purpose);
    console.log('File name: ', fileMetadata.name);
    console.log('File type: ', fileMetadata.mediaType);
    console.log('File size: ', fileMetadata.size);
    const fileBinary = await this.openapiService.getFileContentById(
      customerId,
      fileId,
      purpose,
    );

    if (!fileBinary.success) {
      throw new Error(fileBinary.error);
    }

    const fileName = fileMetadata.name
      ? fileMetadata.name
      : 'file' + fileId + '.' + this.getFileExtension(fileMetadata.mediaType);
    console.log('Download file name: ', fileName);

    return {
      fileId,
      name: fileMetadata.name,
      mediaType: fileMetadata.mediaType,
      size: fileMetadata.size,
      type: purpose,
      file: new File([fileBinary.data], fileName, {
        type: fileMetadata.mediaType,
      }),
      url: URL.createObjectURL(fileBinary.data),
    };
  }

  async getFile(
    fileId: string,
    purpose: FilePurpose,
  ): Promise<DocumentDetails> {
    const user: Profile = this.sharedProfileData.getUserProfile();
    let fileDetails = await this.openapiService.getFileDetailsById(
      user.businessId,
      fileId,
      this.getPurposeType(purpose),
    );
    let fileBinary = await this.openapiService.getFileContentById(
      user.businessId,
      fileId,
      this.getPurposeType(purpose),
    );

    if (!fileBinary.success) {
      throw new Error(fileBinary.error);
    }

    return this.toDocumentDetails(fileDetails.data, fileBinary.data, purpose);
  }

  async deleteFile(fileId: string): Promise<void> {
    try {
      if (fileId.trim().length == 0) {
        return;
      }

      let deleteFileByIdData: DeleteFileByIdData['path'] = {
        file_id: fileId,
      };
      let response =
        await this.openapiService.deleteFileById(deleteFileByIdData);

      if (response.success) {
        console.log('File deleted successfully:', response.data.message);
      } else {
        console.error('File deletion failed:', response.error);
      }
    } catch (error) {
      console.error('Error occurred during file deletion:', error);
      throw error;
    }
  }

  async generateHdfcIrmReport(
    payout: Payout,
    invoice: Invoice,
    bankAccountDetails: PaymentMethodDetails,
    partnerName?: string,
  ): Promise<Blob> {
    const user: Profile = this.sharedProfileData.getUserProfile();

    const valueDate = payout.expectedReceiveDateUTC
      ? moment(payout.expectedReceiveDateUTC).format('DD-MM-YYYY')
      : '';

    // Prepare Excel data payload with required fields
    const excelRow: ExcelRowData = {
      senderRefNo: payout.payoutReferenceId || '',
      inwNo: payout.uniqueTransactionReference || '',
      currency1: payout.grossOrNetPayoutCurrency || 'USD',
      fcyAmount: payout.netPayoutAmount?.toString() || '0',
      accNo: bankAccountDetails.accountNumber || '',
      purposeCode: invoice.purposeCode || '',
      beneficiary: bankAccountDetails.accountName || '',
      valueDate: valueDate,
      remitter: partnerName || '',
      remittingBank: 'JP Morgan Chase',
    };

    const excelData: ExcelDataPayload = {
      rows: [excelRow],
    };

    const requestBody: GenerateHdfcIrmReportRequestContent = {
      purpose: 'HDFC_IRM_REPORT',
      data: excelData,
    };

    const response = await this.openapiService.generateHdfcIrmReport(
      user.businessId,
      requestBody,
    );

    if (!response.success) {
      throw new Error(response.error || 'Error generating HDFC IRM report');
    }

    return response.data;
  }

  async generateHdfcRequestLetter(
    payout: Payout,
    invoice: Invoice,
    bankAccountDetails: PaymentMethodDetails,
    partnerName?: string,
  ): Promise<Blob> {
    const user: Profile = this.sharedProfileData.getUserProfile();

    const payoutSettledDate = payout.expectedReceiveDateUTC
      ? moment(payout.expectedReceiveDateUTC).format('DD-MM-YYYY')
      : '';

    // Prepare request letter payload
    const requestLetterPayload: RequestLetterPayload = {
      payout_settled_date: payoutSettledDate,
      bank_branch: bankAccountDetails.bankAddress || '',
      bank_account_number: bankAccountDetails.accountNumber || '',
      unique_transaction_reference: payout.uniqueTransactionReference || '',
      currency: payout.grossOrNetPayoutCurrency || 'USD',
      transaction_amount: payout.netPayoutAmount?.toString() || '0',
      payout_reference_id: payout.payoutReferenceId || '',
      foreign_remitter: partnerName || '',
      remitting_bank: 'JP Morgan Chase',
      remitting_bank_swift_code: 'CHASUS33XXX',
      beneficiary_account_number: bankAccountDetails.accountNumber || '',
      beneficiary: bankAccountDetails.accountName || '',
      purpose_code: invoice.purposeCode || '',
    };

    const requestBody: GenerateHdfcRequestLetterRequestContent = {
      data: requestLetterPayload,
    };

    const response = await this.openapiService.generateHdfcRequestLetter(
      user.businessId,
      requestBody,
    );

    if (!response.success) {
      throw new Error(response.error || 'Error generating HDFC Request Letter');
    }

    return response.data;
  }
}
